// ConferenceProvider.tsx
import { createContext, FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Device, Call, AudioProcessor  } from "@twilio/voice-sdk";
import { ErrorCallback } from "../../types/twilio-video";
import { PLAYBACKBOT_IDENTITY } from "../../constants";

interface ConferenceContextValue {
	isConnecting: boolean;
	isConnected: boolean;
	hasDevice: boolean;
	connect: (token: string) => Promise<void>;
	disconnect: () => Promise<void>;
	addAudioProcessor: (p: AudioProcessor) => Promise<void>;
	removeAudioProcessor: (p: AudioProcessor) => Promise<void>;
	onError: ErrorCallback;
}

export const ConferenceContext = createContext<ConferenceContextValue>(null!);

interface ConferenceProviderProps {
	onError: ErrorCallback;
	children: ReactNode;
	options: {
		conferenceName: string;
		deviceOptions?: ConstructorParameters<typeof Device>[1];
		extraParams?: Record<string, string>;
	};
}

export const ConferenceProvider: FC<ConferenceProviderProps> = ({ onError, options, children }) => {
	const [isConnecting, setIsConnecting] = useState(false);
	const [isConnected, setIsConnected] = useState(false);
	const [hasDevice, setHasDevice] = useState(false);

	const deviceRef = useRef<Device | null>(null);
	const callRef = useRef<Call | null>(null);
	const activeProcessorRef = useRef<AudioProcessor | null>(null);

	const onErrorCallback: ErrorCallback = useCallback((err) => {
		console.error(`[ConferenceProvider] ${err.message}`, err);
		onError?.(err);
	}, [onError]);

	const ensureDevice = useCallback((token: string) => {
		if (deviceRef.current) {
			return deviceRef.current;
		}

		const device = new Device(token, {
			enableImprovedSignalingErrorPrecision: true,
			codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
			...(options.deviceOptions ?? {})
		});

		device.on("error", (e) => {
			onErrorCallback(e instanceof Error ? e : new Error(typeof e === "string" ? e : JSON.stringify(e)));
		});

		device.audio?.setAudioConstraints?.({ echoCancellation: true });

		deviceRef.current = device;
		setHasDevice(true);

		return device;
	}, [onErrorCallback, options.deviceOptions]);

	const connect = useCallback(async (token: string) => {
		if (!options.conferenceName) {
			throw new Error("ConferenceProvider: options.conferenceName is required.");
		}

		if (callRef.current) {
			console.warn("[ConferenceProvider:connect] Already connected. Disconnecting first.");
			try {
				callRef.current.disconnect();
			} catch (err: any) {
				console.warn("[ConferenceProvider:connect] Failed to disconnect", err?.message ?? err);
			}
			callRef.current = null;
			setIsConnected(false);
		}

		setIsConnecting(true);

		try {
			const device = ensureDevice(token);

			const params = {
				To: `conference:${options.conferenceName}`,
				UserIdentity: PLAYBACKBOT_IDENTITY,
				...(options.extraParams ?? {})
			};

			const call = await device.connect({ params });

			callRef.current = call;

			await new Promise<void>((resolve, reject) => {
				let accepted = false;

				function cleanup() {
					try {
						// eslint-disable-next-line no-use-before-define
						call.off("accept", onAccept);
					} catch (e: any) {
						console.warn("[ConferenceProvider:cleanup] off('accept') failed:", e?.message ?? e);
					}
					try {
						// eslint-disable-next-line no-use-before-define
						call.off("error", onErrorInner);
					} catch (e: any) {
						console.warn("[ConferenceProvider:cleanup] off('error') failed:", e?.message ?? e);
					}
					try {
						// eslint-disable-next-line no-use-before-define
						call.off("disconnect", onDisconnect);
					} catch (e: any) {
						console.warn("[ConferenceProvider:cleanup] off('disconnect') failed:", e?.message ?? e);
					}
				}

				function onAccept() {
					accepted = true;
					setIsConnected(true);
					setIsConnecting(false);
					cleanup();
					resolve();
				}

				function onErrorInner(e: any) {
					const err = e instanceof Error ? e : new Error(e?.message ?? String(e));
					onErrorCallback(err);
					setIsConnecting(false);
					setIsConnected(false);
					cleanup();
					reject(err);
				}

				function onDisconnect() {
					setIsConnected(false);
					callRef.current = null;

					if (!accepted) {
						setIsConnecting(false);
						const err = new Error("Disconnected before accept");
						onErrorCallback(err);
						cleanup();
						reject(err);
					}
				}

				call.on("accept", onAccept);
				call.on("error", onErrorInner);
				call.on("disconnect", onDisconnect);
			});
		} catch (e: any) {
			onErrorCallback(e instanceof Error ? e : new Error(String(e)));
			setIsConnecting(false);
			setIsConnected(false);
			throw e;
		}
	}, [ensureDevice, onErrorCallback, options.conferenceName, options.extraParams]);


	const disconnect = useCallback(async () => {
		// proactively remove any active processor
		if (deviceRef.current && activeProcessorRef.current) {
			try {
				await deviceRef.current.audio?.removeProcessor(activeProcessorRef.current);
			} catch (e: any) {
				console.warn("[ConferenceProvider:disconnect] Failed to remove active processor on disconnect:", e?.message ?? e);
			}
			activeProcessorRef.current = null;
		}

		try {
			callRef.current?.disconnect();
		} catch (e: any) {
			console.warn("[ConferenceProvider:disconnect] Error disconnecting call:", e?.message ?? e);
		}

		callRef.current = null;
		setIsConnected(false);

		try {
			deviceRef.current?.disconnectAll?.();
		} catch (e: any) {
			console.warn("[ConferenceProvider:disconnect] Error disconnectAll:", e?.message ?? e);
		}
	}, []);

	const addAudioProcessor = useCallback(async (processor: AudioProcessor) => {
		if (!deviceRef.current) {
			throw new Error("ConferenceProvider: Device not ready. Connect the conference before adding processors.");
		}

		// Check if device.audio is available
		if (!deviceRef.current.audio) {
			throw new Error("ConferenceProvider: Device audio not available.");
		}

		// Remove existing processor first (SDK supports only one)
		if (activeProcessorRef.current && activeProcessorRef.current !== processor) {
			try {
				await deviceRef.current.audio.removeProcessor(activeProcessorRef.current);
			} catch (e: any) {
				console.warn("[ConferenceProvider:addAudioProcessor] Failed removing existing audio processor:", e?.message ?? e);
			}
			activeProcessorRef.current = null;
		}

		try {
			await deviceRef.current.audio.addProcessor(processor);
			activeProcessorRef.current = processor;
		} catch (e: any) {
			console.warn("[ConferenceProvider:addAudioProcessor] addAudioProcessor failed:", e?.message ?? e);
			throw e;
		}
	}, []);

	const removeAudioProcessor = useCallback(async (p: AudioProcessor) => {
		if (!deviceRef.current) {
			return;
		}

		try {
			await deviceRef.current.audio?.removeProcessor(p);
		} catch (e: any) {
			console.warn("[ConferenceProvider:removeAudioProcessor] removeAudioProcessor failed:", e?.message ?? e);
		} finally {
			if (activeProcessorRef.current === p) {
				activeProcessorRef.current = null;
			}
		}
	}, []);

	// Cleanup on unmount / HMR
	useEffect(() => {
		return () => {
			(async () => {
				// best-effort processor removal
				if (deviceRef.current && activeProcessorRef.current) {
					try {
						await deviceRef.current.audio?.removeProcessor(activeProcessorRef.current);
					} catch (e: any) {
						console.warn("[ConferenceProvider:useEffect] Failed to remove active processor on cleanup:", e?.message ?? e);
					}
					activeProcessorRef.current = null;
				}

				try {
					callRef.current?.disconnect();
				} catch {
					console.warn("[ConferenceProvider:useEffect] Failed to disconnect call");
				}
				try {
					deviceRef.current?.destroy?.();
				} catch {
					console.log("[ConferenceProvider:useEffect] Failed to destroy device");
				}
				callRef.current = null;
				deviceRef.current = null;
				activeProcessorRef.current = null;
				setIsConnected(false);
				setIsConnecting(false);
				setHasDevice(false);
			})();
		};
	}, []);

	const value = useMemo<ConferenceContextValue>(() => ({
		isConnecting,
		isConnected,
		hasDevice,
		connect,
		disconnect,
		addAudioProcessor,
		removeAudioProcessor,
		onError: onErrorCallback
	}), [isConnecting, isConnected, hasDevice, connect, disconnect, addAudioProcessor, removeAudioProcessor, onErrorCallback]);

	return <ConferenceContext.Provider value={value}>{children}</ConferenceContext.Provider>;
};
